import React from 'react'
import { LayoutBand, Cell, FlowGrid, colors, usePageWidth } from '@fs/zion-ui'
import PropTypes from 'prop-types'

// Generic column component
function Column({ children, order, ...props }) {
  return (
    <Cell order={order} {...props}>
      {children}
    </Cell>
  )
}

// Left column component
const LeftColumn = ({ children, order = 0, ...props }) => (
  <Column order={order} {...props}>
    {children}
  </Column>
)

// Right column component
const RightColumn = ({ children, order = 1, ...props }) => (
  <Column order={order} {...props}>
    {children}
  </Column>
)

// Main two-column section component
function TwoColumnSection({
  children,
  gutters = 'md',
  // LayoutBand props
  maxWidth = 'md',
  marginX,
  marginY = 'xxl',
  color = colors.gray02,
  image,
  className,
  style,
  minHeight,
  alignY,
  growY,
}) {
  const columns = usePageWidth()({ default: 1, lg: 2 })
  return (
    <LayoutBand
      maxWidth={maxWidth}
      marginX={marginX}
      marginY={marginY}
      color={color}
      image={image}
      className={className}
      style={style}
      minHeight={minHeight}
      alignY={alignY}
      growY={growY}
    >
      <FlowGrid gutters={gutters} columnCount={columns}>
        {children}
      </FlowGrid>
    </LayoutBand>
  )
}

// Attach sub-components to main component
TwoColumnSection.LeftColumn = LeftColumn
TwoColumnSection.RightColumn = RightColumn

// PropTypes definitions
const columnPropTypes = {
  children: PropTypes.node,
  order: PropTypes.number,
}

Column.propTypes = columnPropTypes
LeftColumn.propTypes = columnPropTypes
RightColumn.propTypes = columnPropTypes

TwoColumnSection.propTypes = {
  children: PropTypes.node,
  gutters: PropTypes.oneOf(['none', 'pico', 'nano', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  // LayoutBand props
  maxWidth: PropTypes.oneOf(['nano', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  marginX: PropTypes.oneOf(['nano', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  marginY: PropTypes.oneOf(['none', 'pico', 'nano', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  color: PropTypes.string,
  image: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  alignY: PropTypes.oneOf(['bottom', 'middle', 'top']),
  growY: PropTypes.bool,
}

export default TwoColumnSection
